import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { FiTrendingUp, FiUsers, FiMessageSquare, FiFilter, FiUserCheck, FiAlertCircle, FiClock, FiActivity, FiCheckCircle, FiBarChart2, FiCalendar, FiPieChart } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';
import { format } from 'date-fns';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [error, setError] = useState(null);

  const fetchAnalyticsData = useCallback(async () => {

    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Fetch dashboard stats
      const statsResponse = await fetch(buildApiUrl(`/api/admin/stats?timeRange=${timeRange}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!statsResponse.ok) {
        throw new Error('Failed to fetch stats');
      }

      const statsData = await statsResponse.json();

      if (statsData.success) {
        setStats(statsData.data);

        // Format user growth data for the chart
        if (statsData.data.userGrowth) {
          const formattedGrowth = statsData.data.userGrowth.map(item => ({
            date: item.date,
            users: item.users
          }));
          setUserGrowth(formattedGrowth);
        }
      }

      // Fetch recent activity
      const activityResponse = await fetch(buildApiUrl('/api/admin/activity'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (activityResponse.ok) {
        const activityData = await activityResponse.json();
        if (activityData.success) {
          const activity = activityData.data.slice(0, 5);
          setRecentActivity(activity); // Show only 5 most recent activities
        }
      }

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Format date to relative time (e.g., '2 hours ago')
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return format(date, 'MMM dd, yyyy');
  };

  // Get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'signup':
        return <FiUsers className="text-blue-500" />;
      case 'login':
        return <FiUserCheck className="text-green-500" />;
      case 'message':
        return <FiMessageSquare className="text-purple-500" />;
      case 'warning':
        return <FiAlertCircle className="text-yellow-500" />;
      default:
        return <FiBarChart2 className="text-gray-500" />;
    }
  };

  const StatCard = ({ title, value, icon: Icon, description }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">
            {value}
          </p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        <div className="p-3 rounded-lg bg-red-50 text-red-600">
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );

  // Refresh icon component
  const FiRefreshCw = (props) => (
    <svg
      {...props}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-xl"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-100 rounded-xl"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <FiAlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={fetchAnalyticsData}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                >
                  Try again <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-gray-500 mt-1">Monitor your platform's performance and user engagement</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-2">
            <div className="relative">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg pl-4 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="90days">Last 90 days</option>
                <option value="12months">Last 12 months</option>
              </select>
              <FiCalendar className="absolute right-3 top-2.5 text-gray-400" />
            </div>
            <button className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
              <FiFilter className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={(stats?.totalUsers ?? 0).toLocaleString()}
            icon={FiUsers}
            description="Registered users"
          />
          <StatCard
            title="Active Users"
            value={(stats?.activeUsers ?? 0).toLocaleString()}
            icon={FiTrendingUp}
            description="Active in last 7 days"
          />
          <StatCard
            title="New Users"
            value={(stats?.newUsers ?? 0).toLocaleString()}
            icon={FiUserCheck}
            description="Last 30 days"
          />
          <StatCard
            title="Completed Profiles"
            value={`${stats?.completedProfiles || '0'}/${stats?.totalProfiles || '0'}`}
            icon={FiPieChart}
            description="Profiles with complete information"
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Growth Chart */}
          <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">User Growth (Last 30 Days)</h2>
              <div className="flex items-center space-x-2">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="7days">Last 7 days</option>
                  <option value="30days">Last 30 days</option>
                  <option value="90days">Last 90 days</option>
                </select>
              </div>
            </div>
            <div className="h-80 flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4">
              {userGrowth.length > 0 ? (
                <div className="w-full h-full flex items-end space-x-1">
                  {userGrowth.map((item, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <div
                        className="w-full bg-gradient-to-t from-red-500 to-red-400 rounded-t-sm"
                        style={{ height: `${Math.min(100, (item.users / Math.max(...userGrowth.map(i => i.users || 1))) * 100)}%` }}
                        title={`${item.users} users on ${item.date}`}
                      ></div>
                      <span className="text-xs text-gray-500 mt-1">{item.date.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center">
                  <FiBarChart2 className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">No user growth data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <button
                onClick={fetchAnalyticsData}
                className="text-sm text-red-600 hover:text-red-700 flex items-center"
              >
                <FiRefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </button>
            </div>
            <div className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start">
                    <div className="p-2 bg-gray-50 rounded-lg mr-3">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <FiClock className="h-3 w-3 mr-1" />
                        {formatRelativeTime(activity.timestamp || new Date())}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <FiActivity className="mx-auto h-8 w-8 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">No recent activity</p>
                </div>
              )}
            </div>
          </div>

          {/* Platform Statistics */}
          <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Messages</p>
                      <p className="text-2xl font-semibold text-gray-900">{(stats?.totalMessages ?? 0).toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      <FiMessageSquare className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-xs text-green-600 font-medium">
                      +{stats?.newMessages || '0'} this week
                    </span>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Pending Reports</p>
                      <p className="text-2xl font-semibold text-gray-900">{(stats?.pendingReports ?? 0).toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-yellow-100 rounded-lg text-yellow-600">
                      <FiAlertCircle className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <a
                      href="/admin/content"
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      Review reports →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex flex-col items-center justify-center">
                <div className="relative w-32 h-32 mb-4">
                  <svg className="w-full h-full" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="3"
                      strokeDasharray={`${(stats?.completedProfiles / (stats?.totalProfiles || 1)) * 100}, 100`}
                      strokeLinecap="round"
                    />
                    <text x="18" y="20.5" textAnchor="middle" fontSize="8" fill="#111827" fontWeight="bold">
                      {stats?.totalProfiles ? Math.round((stats.completedProfiles / stats.totalProfiles) * 100) : 0}%
                    </text>
                    <text x="18" y="25" textAnchor="middle" fontSize="4" fill="#6B7280">
                      Profiles Complete
                    </text>
                  </svg>
                </div>
                <p className="text-sm text-center text-gray-500">
                  {stats?.completedProfiles || '0'} out of {stats?.totalProfiles || '0'} profiles have complete information
                </p>
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">System Health</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg text-green-600 mr-3">
                    <FiCheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">System Status</p>
                    <p className="text-xs text-gray-500">All systems operational</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                  Online
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Database</span>
                  <span className="font-medium text-green-600">Connected</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">API Response</span>
                  <span className="font-medium text-green-600">Fast</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Last Checked</span>
                  <span className="font-medium">{new Date().toLocaleTimeString()}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={fetchAnalyticsData}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FiRefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Analytics;
